import React, { useState, useContext, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ThemeContext } from "styled-components";
import {
  ButtonWithAction,
  Box,
  Stack,
  Module,
  FormContainer,
  FormInputColumn,
  FormInputRow,
  FormInput,
  withWindowSize,
  util,
  constants
} from "@thecb/components";
import AlertBar from "/components/alert-bar";
import TurnstileWidget from "/components/turnstile-widget";
import FormattedCreditCard from "/components/formatted-credit-card";
import { CREDIT_CARD_RESOURCE, SUBMIT_OPERATIONS } from "../../Profile.state";
import S from "/util/Sanctuary";
import { useSelector } from "react-redux";
import {
  getTurnstileSiteKey,
  getTurnstileVerifyURL
} from "../../Profile.selectors";
import { TURNSTILE } from "../../../../../../constants/data_constants";
import {
  required,
  hasLength,
  isValidMonth,
  dateAfterToday
} from "redux-freeform";

const { expirationDateFormat, zipFormat } = util.formats;

const expirationDateErrors = {
  [required.error]: "Expiration date is required",
  [hasLength.error]: "Expiration date is invalid",
  [isValidMonth.error]: "Expiration month is invalid",
  [dateAfterToday.error]: "Expiration date is invalid"
};

const zipCodeErrors = {
  [required.error]: "Zip code is required",
  [hasLength.error]: "Zip code is invalid"
};

const EditCreditCard = ({
  requests,
  actions,
  forms,
  resources,
  resourcesActions,
  alertBars,
  customerManagement,
  handleFocusErrors
}) => {
  let navigate = useNavigate();
  const { id } = useParams();
  const { isMobile } = useContext(ThemeContext);

  const card = resources[CREDIT_CARD_RESOURCE]?.[id];
  const requestStatus = requests[CREDIT_CARD_RESOURCE] || S.RemoteData.NotAsked;

  const {
    forms: { editCreditCardForm: formActions, captchaForm: captchaActions },
    alertBars: { editCreditCardAlertBar: alertBarActions }
  } = actions;
  const { editCreditCardForm: form } = forms;
  const { editCreditCardAlertBar: alertBar } = alertBars;
  const { onSubmitChange } = resourcesActions;

  const [errors, handleErrors] = useState(false);
  const captchaRef = useRef(null);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const siteKey = useSelector(getTurnstileSiteKey);
  const verifyURL = useSelector(getTurnstileVerifyURL);
  const isUS = (form.fields.country?.rawValue ?? "US") === "US";

  useEffect(() => {
    if (card) {
      const month = String(card.expiryMonth).padStart(2, "0");
      const year = String(card.expiryYear).slice(-2);
      formActions.actions.fields.expirationDate.set(month + year);
      // country must be set before zipCode: the zipCode field's constraints
      // are conditional on country's current value, so setting zipCode first
      // would validate against the previous card's stale country and could
      // cause redux-freeform to silently reject/revert the new zip value.
      formActions.actions.fields.country.set(card.country ?? "US");
      formActions.actions.fields.zipCode.set(card.zipCode ?? "");
    }
  }, [id, card]);

  const handleCaptchaExpired = () => {
    captchaActions.actions.fields.captchaToken.set("");
    setIsCaptchaVerified(false);
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
  };

  const handleSubmit = util.general.generateClickHandler(
    form,
    () => {
      handleErrors(true);
      handleFocusErrors(true);
    },
    () => onSubmitChange(CREDIT_CARD_RESOURCE, SUBMIT_OPERATIONS.EDIT, id)
  );

  return (
    <Box padding="0 0 16px" width={isMobile ? "100%" : "576px"} role="main">
      {alertBar.alerts.length > 0 && (
        <Box padding="1rem 0 0">
          <AlertBar {...alertBar} {...alertBarActions} />
        </Box>
      )}
      <Module
        as="h1"
        heading="Edit Credit Card"
        padding="1.5rem"
        color={constants.colors.WHITE}
      >
        {card && (
          <Box padding="0 0 1.5rem">
            <FormattedCreditCard
              lastFour={card.lastFour}
              type={card.cardType}
            />
          </Box>
        )}
        <FormContainer role="form" aria-label="Edit credit card">
          <FormInputColumn>
            <FormInputRow
              breakpoint={isMobile ? "1000rem" : "21rem"}
              childGap={isMobile ? "0rem" : "1rem"}
            >
              <FormInput
                labelTextWhenNoError="Expiration date (MM/YY)"
                dataQa="Expiration date (MM/YY)"
                errorMessages={expirationDateErrors}
                field={form.fields.expirationDate}
                fieldActions={formActions.actions.fields.expirationDate}
                showErrors={errors}
                formatter={expirationDateFormat}
                isNum
                removeFromValue={/\//}
                autocompleteValue="cc-exp"
                onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
                isRequired={true}
              />
            </FormInputRow>
            <Box
              padding={isMobile ? "0" : "0 0.5rem 0 0"}
              width={isMobile ? "100%" : "50%"}
            >
              <FormInput
                isNum={isUS}
                formatter={isUS ? zipFormat : null}
                labelTextWhenNoError="Zip code"
                dataQa="Zip code"
                errorMessages={zipCodeErrors}
                field={form.fields.zipCode}
                fieldActions={formActions.actions.fields.zipCode}
                showErrors={errors}
                onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
                autocompleteValue="billing postal-code"
                isRequired={true}
              />
            </Box>
          </FormInputColumn>
        </FormContainer>
      </Module>
      {siteKey && (
        <TurnstileWidget
          ref={captchaRef}
          verifyURL={verifyURL}
          siteKey={siteKey}
          onSuccess={token => {
            captchaActions.actions.fields.captchaToken.set(token);
            captchaActions.actions.fields.captchaProvider.set(TURNSTILE);
            setIsCaptchaVerified(true);
          }}
          onError={() => {
            captchaActions.actions.fields.captchaToken.set("");
            setIsCaptchaVerified(false);
          }}
          onExpired={handleCaptchaExpired}
          padding="0 1rem 2rem"
        />
      )}
      <Box
        padding="1.5rem 0 0 0"
        borderColor={constants.colors.GHOST_GREY}
        borderSize="1px"
        borderWidthOverride="1px 0 0 0"
      >
        {isMobile ? (
          <Stack childGap="1rem" direction="row">
            <Box width="100%" padding="0">
              <ButtonWithAction
                text="Cancel"
                action={() => navigate(-1)}
                variant="secondary"
                dataQa="Cancel"
                extraStyles={`width: 100%;`}
              />
            </Box>
            <Box width="100%" padding="0">
              <ButtonWithAction
                text="Save"
                action={handleSubmit}
                isLoading={requestStatus.isLoading}
                variant="primary"
                dataQa="Save"
                extraStyles={`width: 100%;`}
                disabled={siteKey && !isCaptchaVerified}
              />
            </Box>
          </Stack>
        ) : (
          <Stack childGap="1rem" direction="row" justify="flex-end">
            <ButtonWithAction
              text="Cancel"
              action={() => navigate(-1)}
              variant="secondary"
              dataQa="Cancel"
            />
            <ButtonWithAction
              text="Save"
              action={handleSubmit}
              isLoading={requestStatus.isLoading}
              variant="primary"
              dataQa="Save"
              disabled={siteKey && !isCaptchaVerified}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default withWindowSize(EditCreditCard);
